import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, Plus, Pencil, Trash2, Eye, Save, Loader2, ArrowLeft, Palette, Type, MousePointerClick } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  stock_alert: 'Alerte Stock',
  health_alert: 'Alerte Santé',
  backup: 'Sauvegarde',
  report: 'Rapport Financier',
};
const TYPE_ICONS = {
  stock_alert: '📦',
  health_alert: '🏥',
  backup: '💾',
  report: '📊',
};

// Color picker field
const ColorField = ({ label, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border cursor-pointer p-0.5"
        data-testid={testId}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-mono text-sm"
        data-testid={`${testId}-text`}
      />
    </div>
  </div>
);

// Template list card
const TemplateCard = ({ template, onEdit, onDelete, onPreview, darkMode }) => (
  <Card
    className={`border ${darkMode ? 'bg-gray-950 border-gray-800' : 'bg-white'} hover:shadow-md transition-shadow`}
    data-testid={`template-card-${template.id}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
            style={{ backgroundColor: template.config?.primary_color + '20' }}
          >
            {TYPE_ICONS[template.type] || '📧'}
          </div>
          <div>
            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {template.name}
            </h3>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {TYPE_LABELS[template.type]} &middot; {new Date(template.updated_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onPreview(template)} data-testid={`preview-btn-${template.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(template)} data-testid={`edit-btn-${template.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(template.id)} data-testid={`delete-btn-${template.id}`} className="text-red-500 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <div className="w-5 h-5 rounded-full border" style={{ backgroundColor: template.config?.primary_color }} title="Couleur primaire" />
        <div className="w-5 h-5 rounded-full border" style={{ backgroundColor: template.config?.secondary_color }} title="Couleur secondaire" />
        <span className={`text-xs ml-auto ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {template.config?.header_title}
        </span>
      </div>
    </CardContent>
  </Card>
);

export default function TemplatesEmail({ user }) {
  const { darkMode } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editor state
  const [editMode, setEditMode] = useState(null); // null | 'create' | 'edit'
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('stock_alert');
  const [editConfig, setEditConfig] = useState({});

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesRes, typesRes] = await Promise.all([
        axios.get(`${API}/email-templates/`),
        axios.get(`${API}/email-templates/types`),
      ]);
      setTemplates(templatesRes.data);
      setTypes(typesRes.data.types);
    } catch (err) {
      toast.error("Erreur lors du chargement des templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const defaultConfigForType = useCallback((type) => {
    const found = types.find(t => t.key === type);
    return found?.default_config || {};
  }, [types]);

  const handleNew = useCallback(() => {
    const defaultType = 'stock_alert';
    setEditMode('create');
    setEditId(null);
    setEditName('');
    setEditType(defaultType);
    setEditConfig(defaultConfigForType(defaultType));
    setPreviewHtml('');
  }, [defaultConfigForType]);

  const handleEdit = useCallback((tpl) => {
    setEditMode('edit');
    setEditId(tpl.id);
    setEditName(tpl.name);
    setEditType(tpl.type);
    setEditConfig(tpl.config || {});
    setPreviewHtml('');
  }, []);

  const handleTypeChange = useCallback((newType) => {
    setEditType(newType);
    setEditConfig(defaultConfigForType(newType));
    setPreviewHtml('');
  }, [defaultConfigForType]);

  // === Preview system using refs to avoid dependency cycles ===
  const previewTimerRef = useRef(null);
  const hasPreviewedRef = useRef(false);
  const editConfigRef = useRef(editConfig);
  const editTypeRef = useRef(editType);
  const editModeRef = useRef(editMode);

  // Keep refs in sync
  useEffect(() => { editConfigRef.current = editConfig; }, [editConfig]);
  useEffect(() => { editTypeRef.current = editType; }, [editType]);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);

  const doPreview = useCallback(async (tpl) => {
    setPreviewLoading(true);
    try {
      const payload = tpl
        ? { type: tpl.type, config: tpl.config }
        : { type: editTypeRef.current, config: editConfigRef.current };
      const res = await axios.post(`${API}/email-templates/preview`, payload);
      setPreviewHtml(res.data.html);
      hasPreviewedRef.current = true;
      if (tpl && !editModeRef.current) {
        setEditMode('edit');
        setEditId(tpl.id);
        setEditName(tpl.name);
        setEditType(tpl.type);
        setEditConfig(tpl.config || {});
      }
    } catch (err) {
      toast.error("Erreur lors de la prévisualisation");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Auto-load preview when entering editor
  useEffect(() => {
    if (editMode && !hasPreviewedRef.current && editType) {
      doPreview(null);
    }
  }, [editMode, editType, doPreview]);

  // Auto-preview: debounced refresh when config changes
  useEffect(() => {
    if (!editMode || !hasPreviewedRef.current) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      doPreview(null);
    }, 600);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [editConfig, editType, editMode, doPreview]);

  const handleSave = useCallback(async () => {
    if (!editName.trim()) {
      toast.error("Le nom du template est requis");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: editName, type: editType, config: editConfig };
      if (editMode === 'create') {
        await axios.post(`${API}/email-templates/`, payload);
        toast.success("Template créé avec succès");
      } else {
        await axios.put(`${API}/email-templates/${editId}`, payload);
        toast.success("Template mis à jour");
      }
      setEditMode(null);
      setPreviewHtml('');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  }, [editMode, editId, editName, editType, editConfig, fetchTemplates]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    try {
      await axios.delete(`${API}/email-templates/${id}`);
      toast.success("Template supprimé");
      fetchTemplates();
      if (editId === id) {
        setEditMode(null);
        setPreviewHtml('');
      }
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  }, [editId, fetchTemplates]);

  const handleBack = useCallback(() => {
    setEditMode(null);
    setPreviewHtml('');
    hasPreviewedRef.current = false;
  }, []);

  const updateConfig = useCallback((key, value) => {
    setEditConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const groupedTemplates = useMemo(() => {
    const groups = {};
    for (const tpl of templates) {
      if (!groups[tpl.type]) groups[tpl.type] = [];
      groups[tpl.type].push(tpl);
    }
    return groups;
  }, [templates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-700 dark:text-slate-300" />
      </div>
    );
  }

  // === EDITOR VIEW ===
  if (editMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {editMode === 'create' ? 'Nouveau Template' : 'Modifier le Template'}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Editor */}
          <div className="space-y-4">
            {/* Basic info */}
            <Card className={darkMode ? 'bg-gray-950 border-gray-800' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Type className="w-4 h-4" /> Informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom du template</Label>
                  <Input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Ex: Template Stock personnalisé"
                    data-testid="template-name-input"
                  />
                </div>
                {editMode === 'create' && (
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={editType} onValueChange={handleTypeChange}>
                      <SelectTrigger data-testid="template-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {TYPE_ICONS[key]} {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Colors */}
            <Card className={darkMode ? 'bg-gray-950 border-gray-800' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Couleurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <ColorField
                    label="Couleur primaire"
                    value={editConfig.primary_color || '#10b981'}
                    onChange={v => updateConfig('primary_color', v)}
                    testId="primary-color"
                  />
                  <ColorField
                    label="Couleur secondaire"
                    value={editConfig.secondary_color || '#059669'}
                    onChange={v => updateConfig('secondary_color', v)}
                    testId="secondary-color"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Texts */}
            <Card className={darkMode ? 'bg-gray-950 border-gray-800' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Type className="w-4 h-4" /> Textes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Titre de l'en-tête</Label>
                  <Input
                    value={editConfig.header_title || ''}
                    onChange={e => updateConfig('header_title', e.target.value)}
                    data-testid="header-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sous-titre</Label>
                  <Input
                    value={editConfig.header_subtitle || ''}
                    onChange={e => updateConfig('header_subtitle', e.target.value)}
                    data-testid="header-subtitle-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message d'introduction</Label>
                  <Textarea
                    value={editConfig.intro_text_alert || ''}
                    onChange={e => updateConfig('intro_text_alert', e.target.value)}
                    rows={2}
                    data-testid="intro-text-input"
                  />
                </div>
                {editType === 'stock_alert' && (
                  <div className="space-y-2">
                    <Label>Message quand tout est OK</Label>
                    <Textarea
                      value={editConfig.intro_text_ok || ''}
                      onChange={e => updateConfig('intro_text_ok', e.target.value)}
                      rows={2}
                      data-testid="intro-ok-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Texte du pied de page</Label>
                  <Input
                    value={editConfig.footer_text || ''}
                    onChange={e => updateConfig('footer_text', e.target.value)}
                    data-testid="footer-text-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Action Button */}
            {editType !== 'report' && (
              <Card className={darkMode ? 'bg-gray-950 border-gray-800' : ''}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MousePointerClick className="w-4 h-4" /> Bouton d'action
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={editConfig.show_action_button || false}
                      onCheckedChange={v => updateConfig('show_action_button', v)}
                      data-testid="show-action-btn-checkbox"
                    />
                    <Label>Afficher le bouton d'action</Label>
                  </div>
                  {editConfig.show_action_button && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Texte du bouton</Label>
                        <Input
                          value={editConfig.action_button_text || ''}
                          onChange={e => updateConfig('action_button_text', e.target.value)}
                          data-testid="action-btn-text-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>URL du bouton (ex: /transactions, /troupeau)</Label>
                        <Input
                          value={editConfig.action_button_url || ''}
                          onChange={e => updateConfig('action_button_url', e.target.value)}
                          placeholder="/transactions"
                          data-testid="action-btn-url-input"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editMode === 'create' ? 'Créer' : 'Enregistrer'}
              </Button>
              <Button
                variant="outline"
                onClick={() => doPreview(null)}
                disabled={previewLoading}
                data-testid="preview-template-btn"
              >
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                Aperçu
              </Button>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="space-y-3">
            <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Aperçu en direct
            </h3>
            <div
              className={`border rounded-xl overflow-hidden ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}
              style={{ minHeight: '500px' }}
              data-testid="template-preview-container"
            >
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full border-0"
                  style={{ minHeight: '600px' }}
                  title="Aperçu du template"
                  data-testid="template-preview-iframe"
                />
              ) : (
                <div className={`flex flex-col items-center justify-center h-96 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Eye className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm">Cliquez sur "Aperçu" pour voir le rendu</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === LIST VIEW ===
  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-email-page">
      {/* En-tête harmonisé */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Templates Email</h1>
        </div>
        {user?.role === 'admin' && (
          <Button onClick={handleNew} className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="new-template-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau Template
          </Button>
        )}
      </div>

      {templates.length === 0 ? (
        <Card className={`${darkMode ? 'bg-gray-950 border-gray-800' : ''}`}>
          <CardContent className="py-16 text-center">
            <Mail className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Aucun template personnalisé
            </h3>
            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Créez votre premier template pour personnaliser les emails d'alertes, sauvegardes et rapports
            </p>
            {user?.role === 'admin' && (
              <Button onClick={handleNew} data-testid="empty-new-template-btn">
                <Plus className="w-4 h-4 mr-2" /> Créer un template
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(TYPE_LABELS).map(([typeKey, typeLabel]) => {
            const tpls = groupedTemplates[typeKey];
            if (!tpls || tpls.length === 0) return null;
            return (
              <div key={typeKey}>
                <h2 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <span>{TYPE_ICONS[typeKey]}</span> {typeLabel}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                    {tpls.length}
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {tpls.map(tpl => (
                    <TemplateCard
                      key={tpl.id}
                      template={tpl}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onPreview={doPreview}
                      darkMode={darkMode}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
